import { useCallback, useEffect, useMemo, useState } from "react";
import { invokeAdmin } from "@/pages/backstage/lib/adminApi";
import { formatAmount, toUsd } from "@/lib/currency";

type PriceRow = {
  key: string;
  category: string;
  label_de: string;
  label_en: string;
  amount: number;
  amount_eur: number | null;
  unit: string;
};

type Props = {
  pin: string | null;
  isDe: boolean;
};

type TransferType = {
  id: string;
  icon: string;
  labelDe: string;
  labelEn: string;
  hintDe: string;
  hintEn: string;
  keys: [string, string, string];
};

// Zentrale Definition der pflegbaren Transferarten. Jede Art hat genau drei
// Preis-Schlüssel (1/2/3 Autos), die in der Datenbank unter baustein_prices
// mit der Kategorie "transfer" liegen.
const TRANSFER_TYPES: TransferType[] = [
  {
    id: "airport",
    icon: "ri-flight-land-line",
    labelDe: "Flughafentransfer",
    labelEn: "Airport transfer",
    hintDe: "Abholung oder Bringung zum Flughafen – inkl. Flugdaten",
    hintEn: "Pickup or drop-off at the airport – incl. flight details",
    keys: ["transfer_1", "transfer_2", "transfer_3"],
  },
  {
    id: "region",
    icon: "ri-map-pin-line",
    labelDe: "Nord-/Ost-/Westbali",
    labelEn: "North/East/West Bali",
    hintDe: "Transfer in die Regionen Nord-, Ost- und Westbali",
    hintEn: "Transfer to the North, East and West Bali regions",
    keys: ["transfer_region_1", "transfer_region_2", "transfer_region_3"],
  },
  {
    id: "hotel",
    icon: "ri-hotel-line",
    labelDe: "Hotel (Umkreis 50 km)",
    labelEn: "Hotel (within 50 km)",
    hintDe: "Abholung von oder zu einem Hotel im Umkreis von 50 km",
    hintEn: "Pickup from or to a hotel within 50 km",
    keys: ["transfer_hotel_1", "transfer_hotel_2", "transfer_hotel_3"],
  },
];

const CAR_COUNTS = [1, 2, 3] as const;

const carLabel = (cars: number, isDe: boolean) =>
  `${cars} ${isDe ? (cars === 1 ? "Auto" : "Autos") : cars === 1 ? "car" : "cars"}`;

export default function TransferPrices({ pin, isDe }: Props) {
  const [prices, setPrices] = useState<Record<string, number>>({});
  const [exchangeRate, setExchangeRate] = useState(1.0753);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadPrices = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data, error: err } = await invokeAdmin<{ prices: PriceRow[] }>({
        action: "list_prices",
        pin,
      });
      if (err) throw new Error(err);
      const rows = data?.prices ?? [];
      const rateRow = rows.find((p) => p.key === "eur_to_usd_rate" || p.unit === "rate");
      const rate = rateRow ? Number(rateRow.amount) : NaN;
      setExchangeRate(Number.isFinite(rate) && rate > 0 ? rate : 1.0753);

      const map: Record<string, number> = {};
      rows
        .filter((p) => p.category === "transfer")
        .forEach((p) => {
          map[p.key] = Number(p.amount) || 0;
        });
      setPrices(map);
    } catch (e) {
      setError(
        isDe
          ? `Transferpreise konnten nicht geladen werden: ${e instanceof Error ? e.message : "Fehler"}`
          : `Could not load transfer prices: ${e instanceof Error ? e.message : "error"}`
      );
    } finally {
      setLoading(false);
    }
  }, [pin, isDe]);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);

  const updatePrice = (key: string, value: string) => {
    const num = value === "" ? 0 : Number(value);
    setPrices((prev) => ({ ...prev, [key]: Number.isFinite(num) ? num : 0 }));
  };

  const allKeys = useMemo(
    () => TRANSFER_TYPES.flatMap((t) => t.keys as unknown as string[]),
    []
  );

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const items = allKeys.map((key) => ({
        key,
        amount: Number(prices[key]) || 0,
      }));
      const { error: err } = await invokeAdmin<{ ok?: boolean }>({
        action: "update_prices",
        pin,
        items,
      });
      if (err) throw new Error(err);
      setNotice(isDe ? "Transferpreise gespeichert." : "Transfer prices saved.");
    } catch (e) {
      setError(
        isDe
          ? `Speichern fehlgeschlagen: ${e instanceof Error ? e.message : "Fehler"}`
          : `Saving failed: ${e instanceof Error ? e.message : "error"}`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-heading text-2xl text-foreground-950">
            {isDe ? "Transferpreise" : "Transfer prices"}
          </h2>
          <p className="text-sm text-foreground-600 mt-1">
            {isDe
              ? "Zentral pflegbar: pro Transferart der Festpreis für 1, 2 oder 3 Autos. Änderungen sind sofort auf der Webseite aktiv."
              : "Maintained centrally: per transfer type the fixed price for 1, 2 or 3 cars. Changes take effect on the website immediately."}
          </p>
          <p className="text-xs text-foreground-500 mt-1">
            {isDe
              ? `Die US-Dollar-Spalte wird mit Ihrem Kurs umgerechnet (1 € = ${exchangeRate} US$).`
              : `The US dollar column is calculated with your rate (1 € = ${exchangeRate} US$).`}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 shrink-0">
          <button
            onClick={loadPrices}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 bg-background-100 hover:bg-background-200 disabled:opacity-60 text-foreground-800 border border-background-300 text-sm font-medium px-5 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
          >
            <i className={loading ? "ri-loader-4-line animate-spin" : "ri-refresh-line"}></i>
            {isDe ? "Aktualisieren" : "Refresh"}
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="inline-flex items-center justify-center gap-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-background-50 text-sm font-medium px-6 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
          >
            {saving && <i className="ri-loader-4-line animate-spin"></i>}
            <i className="ri-save-line"></i>
            {isDe ? "Speichern" : "Save"}
          </button>
        </div>
      </div>

      {notice && (
        <div className="mb-6 text-sm font-medium text-primary-700 bg-primary-100 border border-primary-200 rounded-md px-4 py-3">
          {notice}
        </div>
      )}

      {error && (
        <div className="mb-6 text-sm font-medium text-secondary-700 bg-secondary-100 border border-secondary-200 rounded-md px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <span>{error}</span>
          <button
            onClick={loadPrices}
            className="inline-flex items-center gap-2 bg-background-50 hover:bg-background-100 text-secondary-700 border border-secondary-200 text-sm font-medium px-4 py-2 rounded-md whitespace-nowrap cursor-pointer transition-colors shrink-0"
          >
            <i className="ri-refresh-line"></i>
            {isDe ? "Erneut versuchen" : "Retry"}
          </button>
        </div>
      )}

      <div className="bg-background-100 border border-background-200 rounded-lg px-4 py-3 mb-6 flex items-start gap-3">
        <i className="ri-information-line text-accent-600 text-lg mt-0.5"></i>
        <p className="text-xs text-foreground-600">
          {isDe
            ? "Alle Preise sind Festpreise pro Fahrt. Die maximal mögliche Personenzahl je Auto beträgt 3 Personen (1 Auto = max. 3, 2 Autos = max. 6, 3 Autos = max. 9 Personen)."
            : "All prices are fixed prices per trip. The maximum number of people per car is 3 (1 car = max. 3, 2 cars = max. 6, 3 cars = max. 9 people)."}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-foreground-600 py-8">
          <i className="ri-loader-4-line animate-spin"></i>
          {isDe ? "Wird geladen..." : "Loading..."}
        </div>
      ) : (
        <div className="space-y-5">
          {TRANSFER_TYPES.map((type) => (
            <div
              key={type.id}
              className="bg-background-50 border border-background-200 rounded-lg p-5"
            >
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 text-accent-700 shrink-0">
                  <i className={`${type.icon} text-xl`}></i>
                </div>
                <div className="min-w-0">
                  <h3 className="font-heading text-base text-foreground-950">
                    {isDe ? type.labelDe : type.labelEn}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    {isDe ? type.hintDe : type.hintEn}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {CAR_COUNTS.map((cars, index) => {
                  const key = type.keys[index];
                  const usdValue = toUsd(prices[key] ?? 0, exchangeRate);
                  return (
                    <div
                      key={key}
                      className="rounded-md border border-background-200 bg-background-100 p-4"
                    >
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <span className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                          <i className="ri-car-line text-accent-600"></i>
                          {carLabel(cars, isDe)}
                        </span>
                        <span className="text-[11px] text-foreground-500 whitespace-nowrap">
                          {isDe ? "max." : "max."} {cars * 3} {isDe ? "Pers." : "ppl."}
                        </span>
                      </div>
                      <div className="flex items-end gap-3">
                        <div>
                          <span className="block text-[10px] uppercase tracking-wide text-foreground-500 mb-1 text-center">
                            €
                          </span>
                          <input
                            type="number"
                            min={0}
                            step="1"
                            value={prices[key] ?? 0}
                            onChange={(e) => updatePrice(key, e.target.value)}
                            className="w-24 px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-950 text-right focus:outline-none focus:ring-2 focus:ring-primary-400"
                          />
                        </div>
                        <div>
                          <span className="block text-[10px] uppercase tracking-wide text-foreground-500 mb-1 text-center">
                            US$
                          </span>
                          <div className="w-24 px-3 py-2 rounded-md border border-background-200 bg-background-50 text-sm text-foreground-700 text-right">
                            {formatAmount(usdValue, false)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}